import {
  memo,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { CornerDownLeft, Edit3, Eraser, MessageSquare, X } from "lucide-react";
import {
  focusNextTabCell,
  focusPrevTabCell,
  focusNewRowSku,
} from "../../utils/tabNavigation";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Textarea } from "@stockline/ui/components/textarea";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface M3Comments {
  c1: string;
  c2: string;
  c3: string;
  c4: string;
  c5: string;
  qualite: string;
  description: string;
}

const EMPTY_M3_COMMENTS: M3Comments = {
  c1: "",
  c2: "",
  c3: "",
  c4: "",
  c5: "",
  qualite: "",
  description: "",
};

const M3_COMMENT_KEYS = [
  "c1",
  "c2",
  "c3",
  "c4",
  "c5",
  "qualite",
  "description",
] as const;
const M3_COMMENT_CODES = {
  c1: "TD",
  c2: "CC",
  c3: "F",
  c4: "BP",
  c5: "BL",
  qualite: "Q",
  description: "D",
};

// The c1-c5 slots are user-authored M3 line-text variants (CO01-CO05) that we
// own outright — clearing one must reach M3 so the variant is deleted. They are
// the ONLY clearable slots: `qualite` (ATS101MI) and `description` (ITDS) mirror
// M3's *master* values, so a present-but-empty value there falsely flags the
// order ERP_DIVERGED. Hence empties are kept only for these keys.
const CLEARABLE_M3_COMMENT_KEYS = new Set<string>([
  "c1",
  "c2",
  "c3",
  "c4",
  "c5",
]);

// M3 ITDS field is capped at 30 characters by the ERP. Enforce here so users
// can't type past the limit and so the textarea shows a counter.
const ITDS_MAX_LENGTH = 30;

interface CommentPopoverProps {
  value: string | null | undefined;
  hiddenValue?: string | null | undefined;
  onChange: (value: string) => void;
  onHiddenChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  flowMetadata?: Record<string, any> | null;
  onM3CommentsChange?: (m3Comments: M3Comments) => void;
  isVivoMode?: boolean;
  triggerClassName?: string;
  /** Render as a compact icon-only button (no preview text) */
  iconOnly?: boolean;
}

type TabType = "comment" | "hidden";

/**
 * Pure derivation of trigger-visible state. Shared between the shell
 * (lazy path) and the full editor so both render the identical button
 * face. Keeping it pure means the shell pays no Radix Popover mount cost
 * while still showing exact preview text + border color + AI hints.
 */
function useTriggerDisplay({
  value,
  hiddenValue,
  flowMetadata,
  isVivoMode,
  placeholder,
}: {
  value: string | null | undefined;
  hiddenValue: string | null | undefined;
  flowMetadata: Record<string, any> | null | undefined;
  isVivoMode: boolean;
  placeholder: string;
}) {
  const hasComment = Boolean(value);
  const hasHiddenComment = Boolean(hiddenValue);
  const hasBothComments = hasComment && hasHiddenComment;
  const hasM3Comments =
    isVivoMode &&
    flowMetadata?.m3_comments &&
    M3_COMMENT_KEYS.some((key) => !!flowMetadata.m3_comments[key]);

  const triggerBorderClass = isVivoMode
    ? hasM3Comments
      ? "border-blue-300 hover:border-blue-400 hover:bg-blue-50/50"
      : "border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50"
    : hasComment || hasHiddenComment
      ? "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
      : "border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50";

  const previewText: ReactNode = (() => {
    if (isVivoMode) {
      if (hasM3Comments) {
        const filledKeys = M3_COMMENT_KEYS.filter(
          (key) => !!flowMetadata?.m3_comments?.[key],
        );
        if (filledKeys.length > 0) {
          return (
            <span className="flex items-center gap-1 text-blue-700 truncate">
              {filledKeys.map((key, i) => (
                <span key={key} className="flex items-center gap-0.5 truncate">
                  {i > 0 && (
                    <span className="text-blue-300 flex-shrink-0">|</span>
                  )}
                  <span className="font-medium flex-shrink-0">
                    {M3_COMMENT_CODES[key]}
                  </span>
                  <span className="truncate">
                    {flowMetadata?.m3_comments[key]}
                  </span>
                </span>
              ))}
            </span>
          );
        }
      }
      return placeholder;
    }
    if (hasBothComments) {
      return (
        <span className="flex items-center gap-1 truncate">
          <span className="truncate">{value}</span>
          <span className="text-gray-300 flex-shrink-0">|</span>
          <span className="truncate text-green-700">{hiddenValue}</span>
        </span>
      );
    }
    if (hasComment) return value;
    if (hasHiddenComment) {
      return <span className="text-green-700">{hiddenValue}</span>;
    }
    return placeholder;
  })();

  return {
    hasComment,
    hasHiddenComment,
    hasM3Comments,
    triggerBorderClass,
    previewText,
  };
}

/**
 * Render-only trigger: identical DOM to the full editor's trigger but no
 * Radix `Popover` root. Mounting cost is essentially zero — no Presence
 * `getComputedStyle` reflow, no portal, no focus trap. On the user's
 * first hover/click/focus the parent wrapper hydrates the full
 * `CommentPopoverInner`, which takes over with the editor open.
 */
function CommentPopoverShell({
  value,
  hiddenValue,
  placeholder: placeholderProp,
  disabled = false,
  flowMetadata,
  isVivoMode = false,
  triggerClassName,
  iconOnly = false,
  onActivate,
  onActivateOpen,
}: CommentPopoverProps & {
  onActivate: () => void;
  onActivateOpen: () => void;
}) {
  const { t } = useTranslation();
  const placeholder =
    placeholderProp ??
    (disabled ? t("ordersRecap.noComment") : t("ordersRecap.addComment"));
  const {
    hasComment,
    hasHiddenComment,
    hasM3Comments,
    triggerBorderClass,
    previewText,
  } = useTriggerDisplay({
    value,
    hiddenValue,
    flowMetadata,
    isVivoMode,
    placeholder,
  });

  return (
    <div
      data-testid="recap-comment-trigger"
      data-attr="recap-comment-trigger"
      data-tab-cell
      tabIndex={0}
      aria-readonly={disabled || undefined}
      className={`px-2 py-1 h-8 rounded border cursor-pointer group/cell relative min-w-0 flex items-center transition-colors focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
        disabled
          ? "border-gray-200 bg-gray-50/60 text-gray-500 hover:bg-gray-100/60"
          : triggerBorderClass
      } ${triggerClassName || ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onActivateOpen();
      }}
      onFocus={(e) => {
        if (!iconOnly && e.target === e.currentTarget) onActivate();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onActivateOpen();
        }
        // Tab navigation stays handled by the parent's data-tab-cell
        // traversal; we leave the default tab behavior intact.
      }}
    >
      {iconOnly ? (
        <MessageSquare
          className={`h-3.5 w-3.5 ${
            disabled
              ? "text-gray-400"
              : hasComment || hasHiddenComment || hasM3Comments
                ? "text-blue-500"
                : "text-gray-400"
          }`}
        />
      ) : (
        <div className="flex items-center justify-center w-full min-w-0 relative">
          <div className="flex items-center gap-1 flex-1 min-w-0 pr-1 justify-center">
            <span
              className={`${
                hasComment || hasHiddenComment || hasM3Comments
                  ? disabled
                    ? "text-gray-600 text-xs"
                    : "text-gray-900 text-xs"
                  : "text-gray-400 text-xs"
              } truncate`}
            >
              {previewText}
            </span>
          </div>
          {!disabled && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 transition-all duration-150 ease-in-out">
              <div className="bg-white/90 backdrop-blur-sm rounded px-1 py-0.5 shadow-sm">
                <Edit3 className="h-3 w-3 text-gray-500" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Full editor with Radix `Popover` root + textarea + tab logic. Mounted
 * lazily by the `CommentPopover` wrapper below — partner-card open no
 * longer pays the Radix Presence forced-reflow cost (~12 ms × N flows)
 * for cells that may never be opened.
 */
function CommentPopoverInner({
  value,
  hiddenValue,
  onChange,
  onHiddenChange,
  placeholder: placeholderProp,
  disabled = false,
  flowMetadata,
  onM3CommentsChange,
  isVivoMode = false,
  triggerClassName,
  iconOnly = false,
  openOnMount = false,
}: CommentPopoverProps & { openOnMount?: boolean }) {
  const { t } = useTranslation();
  const placeholder =
    placeholderProp ??
    (disabled ? t("ordersRecap.noComment") : t("ordersRecap.addComment"));
  // `openOnMount` arrives when the lazy wrapper hydrates from a click —
  // start the editor already open so the user's single click lands them
  // in an editable state (matches the pre-lazy UX, no double-click race).
  const [open, setOpen] = useState(openOnMount);
  const [activeTab, setActiveTab] = useState<TabType>("comment");
  const [activeM3Tab, setActiveM3Tab] =
    useState<(typeof M3_COMMENT_KEYS)[number]>("c1");
  const [localValue, setLocalValue] = useState(value || "");
  const [localHiddenValue, setLocalHiddenValue] = useState(hiddenValue || "");
  const [localM3Comments, setLocalM3Comments] =
    useState<M3Comments>(EMPTY_M3_COMMENTS);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);

  const hasHiddenComments = onHiddenChange !== undefined;
  const {
    hasComment,
    hasHiddenComment,
    hasM3Comments,
    triggerBorderClass,
    previewText,
  } = useTriggerDisplay({
    value,
    hiddenValue,
    flowMetadata,
    isVivoMode,
    placeholder,
  });

  // Read saved tab from localStorage when popover opens
  useEffect(() => {
    if (open) {
      if (isVivoMode) {
        const existing = flowMetadata?.m3_comments;
        setLocalM3Comments(
          existing
            ? { ...EMPTY_M3_COMMENTS, ...existing }
            : { ...EMPTY_M3_COMMENTS },
        );
        return;
      }
      if (!hasHiddenComments) {
        setActiveTab("comment");
        return;
      }
      try {
        const saved = localStorage.getItem("recap-comment-tab");
        if (saved === "hidden" || saved === "comment") {
          setActiveTab(saved);
        }
      } catch {}
    }
  }, [open, hasHiddenComments, isVivoMode, flowMetadata]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    try {
      localStorage.setItem("recap-comment-tab", tab);
    } catch {}
  };

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  useEffect(() => {
    setLocalHiddenValue(hiddenValue || "");
  }, [hiddenValue]);

  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [open, activeTab, activeM3Tab]);

  const m3CommentsEqual = (a: M3Comments, b: M3Comments) =>
    M3_COMMENT_KEYS.every((k) => (a[k] ?? "") === (b[k] ?? ""));

  // Decide which M3 comment keys to persist.
  //
  // A filled key is always shipped. An EMPTY key is shipped only when it is a
  // clearable comment slot (c1-c5) that PREVIOUSLY held a value — i.e. a
  // genuine clear. Shipping it as an explicit "" lets the ERP modify path
  // delete the M3 text variant (AddText TEXT='' REPL=1); on the next readback
  // the variant is absent and the slot collapses back to empty, so the state
  // converges with no false divergence (the check normalises ""/None alike).
  //
  // Empty `description`/`qualite` stay pruned: they mirror M3 master values, so
  // a present-but-empty value there would flag the order ERP_DIVERGED. And an
  // empty c1-c5 that was never set is dropped too — there is nothing to clear,
  // and sending "" would fire a no-op clear against a non-existent variant.
  const pruneEmptyM3Comments = (c: M3Comments): M3Comments => {
    const saved = flowMetadata?.m3_comments ?? {};
    return Object.fromEntries(
      M3_COMMENT_KEYS.filter((k) => {
        if ((c[k] ?? "").trim() !== "") return true;
        return (
          CLEARABLE_M3_COMMENT_KEYS.has(k) &&
          String(saved[k] ?? "").trim() !== ""
        );
      }).map((k) => [k, c[k] ?? ""]),
    ) as unknown as M3Comments;
  };

  /**
   * Persist any pending edits and toast on real change. No-op when read-only.
   * Vivo mode saves the m3_comments object as a unit; normal mode saves both
   * visible + hidden tabs, since users often type in both before validating.
   */
  const commitPendingEdits = (): void => {
    if (disabled) return;
    let changed = false;
    if (isVivoMode) {
      const savedM3 = flowMetadata?.m3_comments
        ? { ...EMPTY_M3_COMMENTS, ...flowMetadata.m3_comments }
        : EMPTY_M3_COMMENTS;
      if (!m3CommentsEqual(localM3Comments, savedM3)) {
        onM3CommentsChange?.(pruneEmptyM3Comments(localM3Comments));
        changed = true;
      }
    } else {
      if (localValue !== (value || "")) {
        onChange(localValue);
        changed = true;
      }
      if (onHiddenChange && localHiddenValue !== (hiddenValue || "")) {
        onHiddenChange(localHiddenValue);
        changed = true;
      }
    }
    if (changed) {
      toast.success(t("ordersRecap.commentSaved"), { duration: 2000 });
    }
  };

  const saveAndClose = () => {
    commitPendingEdits();
    closingRef.current = true;
    setOpen(false);
  };

  const revertAndClose = useCallback(() => {
    setLocalValue(value || "");
    setLocalHiddenValue(hiddenValue || "");
    const existing = flowMetadata?.m3_comments;
    setLocalM3Comments(
      existing
        ? { ...EMPTY_M3_COMMENTS, ...existing }
        : { ...EMPTY_M3_COMMENTS },
    );
    closingRef.current = true;
    setOpen(false);
  }, [value, hiddenValue, flowMetadata]);

  const handleSave = () => {
    saveAndClose();
  };

  // Empty the CURRENT tab only (visible / hidden, or the active M3 field).
  // Stays local — like any other edit, it persists on Save/blur and does
  // NOT close the popover (the buttons preventDefault on mousedown, so the
  // textarea keeps focus). Other tabs are untouched.
  const handleClearCurrent = () => {
    if (disabled) return;
    if (isVivoMode) {
      setLocalM3Comments((prev) => ({ ...prev, [activeM3Tab]: "" }));
    } else if (activeTab === "hidden") {
      setLocalHiddenValue("");
    } else {
      setLocalValue("");
    }
  };

  let currentTabValue: string;
  if (isVivoMode) {
    currentTabValue = localM3Comments[activeM3Tab];
  } else if (activeTab === "hidden") {
    currentTabValue = localHiddenValue;
  } else {
    currentTabValue = localValue;
  }
  const canClearCurrent = currentTabValue.length > 0;

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    // Don't save if focus moves within the popover or back to the trigger
    if (relatedTarget && popoverContentRef.current?.contains(relatedTarget)) {
      return;
    }
    if (relatedTarget && triggerDivRef.current?.contains(relatedTarget)) {
      return;
    }
    saveAndClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      saveAndFocusNewRow();
    } else if (e.key === "Tab") {
      e.preventDefault();
      if (isVivoMode) {
        // Vivo mode: cycle through M3 tabs (c1→c2→c3→c4→c5), then exit
        const currentIdx = M3_COMMENT_KEYS.indexOf(activeM3Tab);
        if (e.shiftKey) {
          if (currentIdx > 0) {
            // Save current tab's value, move to previous M3 tab
            if (!disabled)
              onM3CommentsChange?.(pruneEmptyM3Comments(localM3Comments));
            setActiveM3Tab(M3_COMMENT_KEYS[currentIdx - 1]);
          } else {
            // On first M3 tab + Shift+Tab → save and exit to prev cell
            saveAndClose();
            requestAnimationFrame(() => {
              if (triggerDivRef.current)
                focusPrevTabCell(triggerDivRef.current);
            });
          }
        } else {
          if (currentIdx < M3_COMMENT_KEYS.length - 1) {
            // Save current tab's value, move to next M3 tab
            if (!disabled)
              onM3CommentsChange?.(pruneEmptyM3Comments(localM3Comments));
            setActiveM3Tab(M3_COMMENT_KEYS[currentIdx + 1]);
          } else {
            // On last M3 tab + Tab → save and exit to next cell
            saveAndClose();
            requestAnimationFrame(() => {
              if (triggerDivRef.current)
                focusNextTabCell(triggerDivRef.current);
            });
          }
        }
      } else if (hasHiddenComments) {
        // Normal mode with 2 tabs: cycle comment → hidden, then exit
        if (e.shiftKey) {
          if (activeTab === "hidden") {
            // Save hidden, switch to comment tab
            if (!disabled && onHiddenChange) onHiddenChange(localHiddenValue);
            handleTabChange("comment");
          } else {
            // On comment tab + Shift+Tab → save and exit to prev cell
            saveAndClose();
            requestAnimationFrame(() => {
              if (triggerDivRef.current)
                focusPrevTabCell(triggerDivRef.current);
            });
          }
        } else {
          if (activeTab === "comment") {
            // Save comment, switch to hidden tab
            if (!disabled) onChange(localValue);
            handleTabChange("hidden");
          } else {
            // On hidden tab + Tab → save and exit to next cell
            saveAndClose();
            requestAnimationFrame(() => {
              if (triggerDivRef.current)
                focusNextTabCell(triggerDivRef.current);
            });
          }
        }
      } else {
        // Single tab mode: save and exit
        saveAndClose();
        requestAnimationFrame(() => {
          if (triggerDivRef.current) {
            if (e.shiftKey) {
              focusPrevTabCell(triggerDivRef.current);
            } else {
              focusNextTabCell(triggerDivRef.current);
            }
          }
        });
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setLocalValue(value || "");
      setLocalHiddenValue(hiddenValue || "");
      closingRef.current = true;
      setOpen(false);
    }
  };

  const currentValue = activeTab === "comment" ? localValue : localHiddenValue;
  const currentOnChange =
    activeTab === "comment" ? setLocalValue : setLocalHiddenValue;
  const currentPlaceholder =
    activeTab === "comment"
      ? t("ordersRecap.commentPlaceholder")
      : t("ordersRecap.hiddenCommentPlaceholder");

  // Track the trigger element for focusNewRowSku after save
  const triggerDivRef = useRef<HTMLDivElement>(null);

  const saveAndFocusNewRow = useCallback(() => {
    commitPendingEdits();
    closingRef.current = true;
    setOpen(false);
    // Focus new row SKU after closing
    requestAnimationFrame(() => {
      if (triggerDivRef.current) {
        focusNewRowSku(triggerDivRef.current);
      }
    });
  }, [
    disabled,
    isVivoMode,
    flowMetadata,
    localM3Comments,
    onM3CommentsChange,
    value,
    localValue,
    onChange,
    hiddenValue,
    localHiddenValue,
    onHiddenChange,
    t,
  ]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={triggerDivRef}
          data-testid="recap-comment-trigger"
          data-attr="recap-comment-trigger"
          data-tab-cell
          tabIndex={0}
          aria-readonly={disabled || undefined}
          className={`px-2 py-1 h-8 rounded border cursor-pointer group/cell relative min-w-0 flex items-center transition-colors focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 data-[state=open]:bg-blue-50/60 data-[state=open]:border-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-500 ${
            disabled
              ? "border-gray-200 bg-gray-50/60 text-gray-500 hover:bg-gray-100/60"
              : triggerBorderClass
          } ${triggerClassName || ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          onFocus={(e) => {
            // Auto-open when focused via keyboard Tab in table cells (not header icon mode)
            // Skip if focus returned after an intentional close (validate/cancel button)
            if (closingRef.current) {
              closingRef.current = false;
              return;
            }
            if (!iconOnly && !open && e.target === e.currentTarget) {
              setOpen(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !open) {
              e.preventDefault();
              if (e.shiftKey) {
                focusPrevTabCell(e.currentTarget);
              } else {
                focusNextTabCell(e.currentTarget);
              }
            } else if (e.key === "Enter" && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          {iconOnly ? (
            <MessageSquare
              className={`h-3.5 w-3.5 ${
                disabled
                  ? "text-gray-400"
                  : hasComment || hasHiddenComment || hasM3Comments
                    ? "text-blue-500"
                    : "text-gray-400"
              }`}
            />
          ) : (
            <div className="flex items-center justify-center w-full min-w-0 relative">
              <div className="flex items-center gap-1 flex-1 min-w-0 pr-1 justify-center">
                <span
                  className={`${
                    hasComment || hasHiddenComment || hasM3Comments
                      ? disabled
                        ? "text-gray-600 text-xs"
                        : "text-gray-900 text-xs"
                      : "text-gray-400 text-xs"
                  } truncate`}
                >
                  {previewText}
                </span>
              </div>
              {!disabled && (
                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 transition-all duration-150 ease-in-out">
                  <div className="bg-white/90 backdrop-blur-sm rounded px-1 py-0.5 shadow-sm">
                    <Edit3 className="h-3 w-3 text-gray-500" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverContentRef}
        className={`p-0 shadow-lg bg-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${
          isVivoMode
            ? "w-max min-w-[420px] max-w-[95vw] border-2 border-blue-400"
            : "w-[420px] border-2 border-blue-400"
        }`}
        align="center"
        side="top"
        sideOffset={4}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
      >
        {isVivoMode ? (
          <>
            <div className="flex border-b border-gray-200">
              {M3_COMMENT_KEYS.map((key) => {
                const hasContent = !!localM3Comments[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setActiveM3Tab(key);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    className={`flex-shrink-0 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors ${
                      activeM3Tab === key
                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t(`ordersRecap.m3CommentTypes.${key}`)}
                    {hasContent && (
                      <span className="ml-1 w-1.5 h-1.5 bg-blue-500 rounded-full inline-block" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="p-1.5">
              <Textarea
                ref={textareaRef}
                value={localM3Comments[activeM3Tab]}
                onChange={(e) =>
                  setLocalM3Comments((prev) => ({
                    ...prev,
                    [activeM3Tab]: e.target.value,
                  }))
                }
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={t(`ordersRecap.m3CommentTypes.${activeM3Tab}`)}
                readOnly={disabled}
                maxLength={
                  activeM3Tab === "description" ? ITDS_MAX_LENGTH : undefined
                }
                className={`min-h-[120px] max-h-[320px] resize-none border-none focus-visible:ring-0 focus-visible:outline-none text-sm leading-relaxed ${
                  disabled ? "bg-gray-50 text-gray-700 cursor-default" : ""
                }`}
              />
              {activeM3Tab === "description" && (
                <div className="px-1 pb-1 text-[10px] text-gray-400">
                  {localM3Comments.description.length}/{ITDS_MAX_LENGTH}
                </div>
              )}
              <PopoverActions
                disabled={disabled}
                onSave={handleSave}
                onCancel={revertAndClose}
                onClear={handleClearCurrent}
                canClear={canClearCurrent}
                t={t}
              />
            </div>
          </>
        ) : (
          <>
            {hasHiddenComments && (
              <div className="flex border-b border-gray-200">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTabChange("comment");
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === "comment"
                      ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t("ordersRecap.comment")}
                  {hasComment && (
                    <span className="ml-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full inline-block" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTabChange("hidden");
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === "hidden"
                      ? "text-green-600 border-b-2 border-green-600 bg-green-50/50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t("ordersRecap.hiddenComment")}
                  {hasHiddenComment && (
                    <span className="ml-1.5 w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                  )}
                </button>
              </div>
            )}
            <div className="p-1.5">
              <Textarea
                ref={textareaRef}
                value={currentValue}
                onChange={(e) => currentOnChange(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={currentPlaceholder}
                readOnly={disabled}
                className={`min-h-[120px] max-h-[320px] resize-none border-none focus-visible:ring-0 focus-visible:outline-none text-sm leading-relaxed ${
                  disabled ? "bg-gray-50 text-gray-700 cursor-default" : ""
                }`}
              />
              <PopoverActions
                disabled={disabled}
                onSave={handleSave}
                onCancel={revertAndClose}
                onClear={handleClearCurrent}
                canClear={canClearCurrent}
                t={t}
              />
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface PopoverActionsProps {
  disabled: boolean;
  onSave: () => void;
  onCancel: () => void;
  onClear: () => void;
  canClear: boolean;
  t: (key: string) => string;
}

function PopoverActions({
  disabled,
  onSave,
  onCancel,
  onClear,
  canClear,
  t,
}: PopoverActionsProps) {
  if (disabled) {
    return (
      <div className="flex items-center justify-between px-1 pb-1 pt-0.5">
        <span className="text-[11px] text-gray-500">
          {t("ordersRecap.commentReadOnly")}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-1.5 px-1 pt-2 pb-1.5">
      {/* Discrete clear-current-tab affordance; hidden when nothing to clear */}
      {canClear ? (
        <button
          data-testid="recap-comment-clear-button"
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClear}
          className="flex items-center gap-1 px-1.5 py-1 text-xs font-medium text-gray-400 hover:text-red-600 transition-colors"
        >
          <Eraser className="h-3 w-3" strokeWidth={1.5} />
          {t("ordersRecap.commentClear")}
        </button>
      ) : (
        <span />
      )}
      <div className="flex justify-end gap-1.5">
        <button
          data-testid="recap-comment-cancel-button"
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onCancel}
          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded transition-colors"
        >
          {t("ordersRecap.commentCancel")}
          <X className="h-3 w-3" strokeWidth={1.5} />
        </button>
        <button
          data-testid="recap-comment-save-button"
          data-attr="recap-comment-save-button"
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSave}
          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-600 hover:bg-green-100 rounded transition-colors"
        >
          {t("ordersRecap.commentValidate")}
          <CornerDownLeft className="h-3 w-3" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

/**
 * Lazy wrapper: mounts `CommentPopoverShell` (zero Radix cost) until the
 * user's first hover/focus/click, then swaps to `CommentPopoverInner`
 * (full Radix Popover, textarea, M3 tab logic). For a 4-flow partner
 * card this skips ~48 ms of `getAnimationName` forced reflows on open.
 *
 * The activation source decides whether the editor opens on hydration:
 * - "click" → `openOnMount={true}` so a single click lands on the open
 *   editor (matches pre-lazy UX, no double-click race)
 * - "focus" / "hover" → editor mounts closed; the user's next gesture
 *   opens it via the normal trigger path
 *
 * Memoised so a sibling cell's edit doesn't bounce the hydration state.
 */
type CommentActivation = "click" | "focus" | "hover";

function CommentPopoverLazy(props: CommentPopoverProps) {
  const [activation, setActivation] = useState<CommentActivation | null>(null);

  const activate = useCallback(() => {
    setActivation((prev) => prev ?? "focus");
  }, []);
  const activateOpen = useCallback(() => setActivation("click"), []);
  const activateHover = useCallback(() => {
    setActivation((prev) => prev ?? "hover");
  }, []);

  if (activation) {
    return (
      <CommentPopoverInner {...props} openOnMount={activation === "click"} />
    );
  }
  // Hover-hydrate so a mouse user's click lands on the real Radix
  // trigger (the shell unmount/inner-mount has already happened during
  // the ~100 ms pointer travel). Keyboard users hit `activate` via focus.
  return (
    <div onPointerEnter={activateHover}>
      <CommentPopoverShell
        {...props}
        onActivate={activate}
        onActivateOpen={activateOpen}
      />
    </div>
  );
}

export const CommentPopover = memo(CommentPopoverLazy);
